import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Loader2, Star, Check, XCircle, Copy, Mail, Inbox, Trash2, Clock } from 'lucide-react';
import { countriesFor, countryCodeOf, countryName } from '../lib/countries';
import { Flag } from '../components/Flag';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  requested: { label: 'Solicitada', cls: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  received: { label: 'Recibida · pendiente de aprobar', cls: 'bg-[#E8F5FA] text-[#7DBDD9] border-[#A8D8EA]' },
  published: { label: 'Publicada', cls: 'bg-green-50 text-green-700 border-green-200' },
  rejected: { label: 'Rechazada', cls: 'bg-red-50 text-red-600 border-red-200' },
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

export default function AdminReviews() {
  const { token } = useAuth();
  const authHeaders = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const fetchReviews = React.useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/reviews`, authHeaders);
      setReviews(data);
    } catch {
      toast.error('Error al cargar las reseñas');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  const fetchSettings = React.useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/settings/reviews`, authHeaders);
      setSettings(data);
    } catch {
      setSettings(null);
    }
  }, [authHeaders]);

  useEffect(() => { fetchReviews(); fetchSettings(); }, [fetchReviews, fetchSettings]);

  const saveSettings = async (patch) => {
    setSavingSettings(true);
    try {
      const { data } = await axios.put(`${API}/settings/reviews`, { ...settings, ...patch }, authHeaders);
      setSettings(data);
      toast.success(data.auto_request_enabled
        ? `Pediré la reseña automáticamente tras la clase n.º ${data.min_classes}`
        : 'Peticiones automáticas desactivadas');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSavingSettings(false);
    }
  };

  const setStatus = async (r, status) => {
    setBusyId(r.id);
    try {
      await axios.patch(`${API}/reviews/${r.id}`, { status }, authHeaders);
      toast.success(status === 'published' ? 'Reseña publicada en la web' : 'Reseña rechazada');
      fetchReviews();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo actualizar');
    } finally {
      setBusyId(null);
    }
  };

  const setCountry = async (r, code) => {
    setBusyId(r.id);
    try {
      await axios.patch(`${API}/reviews/${r.id}`, {
        country_code: code, country: code ? countryName(code, 'es') : null,
      }, authHeaders);
      toast.success(code ? `País actualizado: ${countryName(code, 'es')}` : 'País eliminado');
      fetchReviews();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo actualizar el país');
    } finally {
      setBusyId(null);
    }
  };

  const resend = async (r) => {
    setBusyId(r.id);
    try {
      await axios.post(`${API}/reviews/${r.id}/send-email`, {}, authHeaders);
      toast.success(`Solicitud enviada a ${r.email}`);
      fetchReviews();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo enviar el email');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`¿Eliminar la reseña de ${r.name}?`)) return;
    setBusyId(r.id);
    try {
      await axios.delete(`${API}/reviews/${r.id}`, authHeaders);
      toast.success('Reseña eliminada');
      fetchReviews();
    } catch {
      toast.error('No se pudo eliminar');
    } finally {
      setBusyId(null);
    }
  };

  const copyLink = async (r) => {
    try {
      await navigator.clipboard.writeText(r.review_url);
      toast.success('Enlace copiado');
    } catch {
      toast.error('No se pudo copiar el enlace');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-[#7DBDD9] animate-spin" /></div>;
  }

  const pending = reviews.filter((r) => r.status === 'received').length;

  return (
    <div data-testid="admin-reviews">
      <div className="mb-6">
        <h2 className="text-lg font-bold text-[#4A4A4A]">Reseñas de alumnos</h2>
        <p className="text-sm text-gray-500">
          {reviews.length} solicitud{reviews.length === 1 ? '' : 'es'} ·{' '}
          {reviews.filter((r) => r.status === 'published').length} publicadas
          {pending > 0 && <span className="text-[#7DBDD9] font-semibold"> · {pending} por revisar</span>}
        </p>
        <p className="text-xs text-gray-400 mt-1">
          Pide reseñas desde la pestaña <strong>Alumnos</strong>, con el botón “Solicitar reseña”.
        </p>
      </div>

      {settings && (
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-5" data-testid="auto-review-settings">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-bold text-[#4A4A4A] text-sm">Pedir reseña automáticamente</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Cada día a las 18:00 reviso quién ya ha dado {settings.min_classes} clases y le envío el enlace por email, una sola vez por alumno.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={settings.min_classes}
                onChange={(e) => saveSettings({ min_classes: Number(e.target.value) })}
                disabled={savingSettings}
                data-testid="auto-review-min-classes"
                className="px-3 py-2 rounded-xl border border-gray-200 text-sm text-[#4A4A4A] disabled:opacity-50"
              >
                {[2, 3, 4, 5, 8, 10].map((n) => (
                  <option key={n} value={n}>tras {n} clases</option>
                ))}
              </select>
              <button
                onClick={() => saveSettings({ auto_request_enabled: !settings.auto_request_enabled })}
                disabled={savingSettings}
                data-testid="auto-review-toggle"
                className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 ${
                  settings.auto_request_enabled
                    ? 'bg-green-50 text-green-700 hover:bg-green-100'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />}
                {settings.auto_request_enabled ? 'Activado' : 'Desactivado'}
              </button>
            </div>
          </div>
        </div>
      )}

      {reviews.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-100" data-testid="no-reviews">
          <Inbox className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Todavía no has solicitado ninguna reseña.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.map((r, index) => {
            const st = STATUS[r.status] || STATUS.requested;
            return (
              <div key={r.id} data-testid={`review-card-${index}`} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="font-bold text-[#4A4A4A]">{r.name}</span>
                  <span className={`px-2 py-1 rounded-md text-xs font-semibold border ${st.cls}`} data-testid={`review-status-${index}`}>
                    {st.label}
                  </span>
                  <span className="px-2 py-1 rounded-md text-xs font-semibold bg-gray-100 text-gray-600 uppercase">{r.language}</span>
                  {r.rating && (
                    <span className="flex items-center gap-0.5" data-testid={`review-rating-${index}`}>
                      {[...Array(r.rating)].map((_, i) => (
                        <Star key={i} className="w-3.5 h-3.5 text-[#F5C14B] fill-[#F5C14B]" />
                      ))}
                    </span>
                  )}
                </div>

                <div className="text-sm text-gray-500 flex flex-wrap gap-x-4 gap-y-1 mb-3">
                  <a href={`mailto:${r.email}`} className="hover:text-[#7DBDD9] flex items-center gap-1.5"><Mail className="w-4 h-4" />{r.email}</a>
                  <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" />Solicitada: {fmt(r.requested_at)}</span>
                  {r.email_sent_at && <span>Email enviado: {fmt(r.email_sent_at)}</span>}
                  {r.submitted_at && <span>Recibida: {fmt(r.submitted_at)}</span>}
                </div>

                {r.text && (
                  <blockquote className="px-4 py-3 rounded-xl bg-[#E8F5FA] text-sm text-[#4A4A4A] mb-3" data-testid={`review-text-${index}`}>
                    “{r.text}”
                    <span className="block mt-2 text-xs text-gray-500">
                      Se publicará como: <strong>{r.display_name_mode === 'full' ? r.name : r.name.split(' ')[0]}</strong>
                    </span>
                    <span className="block mt-2 text-xs text-gray-500">
                      País:{' '}
                      <select
                        value={r.country_code || countryCodeOf(r.country) || ''}
                        onChange={(e) => setCountry(r, e.target.value)}
                        disabled={busyId === r.id}
                        data-testid={`review-country-select-${index}`}
                        className="ml-1 px-2 py-1 rounded-lg border border-gray-200 text-xs text-[#4A4A4A] bg-white disabled:opacity-50"
                      >
                        <option value="">Sin país</option>
                        {countriesFor('es').map((item) => (
                          <option key={item.code} value={item.code}>{item.es}</option>
                        ))}
                      </select>
                      {(r.country_code || countryCodeOf(r.country)) && (
                        <Flag code={r.country_code || countryCodeOf(r.country)} title={r.country} className="ml-2" />
                      )}
                    </span>
                  </blockquote>
                )}

                <div className="flex flex-wrap gap-2">
                  {r.status === 'received' && (
                    <>
                      <button
                        onClick={() => setStatus(r, 'published')}
                        disabled={busyId === r.id}
                        data-testid={`review-publish-${index}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-50 hover:bg-green-100 text-green-700 text-xs font-semibold transition-colors disabled:opacity-50"
                      >
                        <Check className="w-3.5 h-3.5" />Publicar en la web
                      </button>
                      <button
                        onClick={() => setStatus(r, 'rejected')}
                        disabled={busyId === r.id}
                        data-testid={`review-reject-${index}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 text-xs font-semibold transition-colors disabled:opacity-50"
                      >
                        <XCircle className="w-3.5 h-3.5" />Rechazar
                      </button>
                    </>
                  )}
                  {r.status === 'published' && (
                    <button
                      onClick={() => setStatus(r, 'received')}
                      disabled={busyId === r.id}
                      data-testid={`review-unpublish-${index}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-semibold transition-colors disabled:opacity-50"
                    >
                      <XCircle className="w-3.5 h-3.5" />Quitar de la web
                    </button>
                  )}
                  {r.status === 'rejected' && (
                    <button
                      onClick={() => setStatus(r, 'published')}
                      disabled={busyId === r.id}
                      data-testid={`review-republish-${index}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-50 hover:bg-green-100 text-green-700 text-xs font-semibold transition-colors disabled:opacity-50"
                    >
                      <Check className="w-3.5 h-3.5" />Publicar igualmente
                    </button>
                  )}
                  <button
                    onClick={() => copyLink(r)}
                    data-testid={`review-copy-${index}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E8F5FA] hover:bg-[#A8D8EA]/40 text-[#7DBDD9] text-xs font-semibold transition-colors"
                  >
                    <Copy className="w-3.5 h-3.5" />Copiar enlace
                  </button>
                  {r.status === 'requested' && (
                    <button
                      onClick={() => resend(r)}
                      disabled={busyId === r.id}
                      data-testid={`review-resend-${index}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-semibold transition-colors disabled:opacity-50"
                    >
                      <Mail className="w-3.5 h-3.5" />Enviar por email
                    </button>
                  )}
                  <button
                    onClick={() => remove(r)}
                    disabled={busyId === r.id}
                    data-testid={`review-delete-${index}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 text-xs font-semibold transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="w-3.5 h-3.5" />Eliminar
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
